/**
 * Calendar List Component
 * Lists calendars grouped by integration with optional click-to-select functionality
 * Uses CalendarsContext for data fetching
 * 
 * Props:
 * - isSelectable: Enable click-to-select mode with arrow indicators (default: false)
 * - isLocked: When true, selection is disabled even in selectable mode (default: false)
 * - showDisconnect: Show disconnect account button below each integration (default: false)
 * 
 * Can be used by: AutomationRules, LifeDomains, CalendarsIntegrations, etc.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useCalendars, type CalendarResource } from '@/context/calendars-context'
import { RefreshCw, Trash2, LightningIcon, InsightsIcon, CompassIcon, ChevronRight, CalendarAddIcon } from '@/components/icons'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface CalendarListProps {
  /** Currently selected calendar (composite key: integration_id:calendar_id) */
  selectedCalendarId?: string | null
  /** Callback when selection changes */
  onChange?: (calendarId: string | null) => void
  /** Additional CSS classes */
  className?: string
  /** Disable all interactions */
  disabled?: boolean
  /** Show radio buttons for selection (default: false) */
  isSelectable?: boolean
  /** When true, selection is disabled even with radio buttons visible (default: false) */
  isLocked?: boolean
  /** Show disconnect account button below each integration's calendars (default: false) */
  showDisconnect?: boolean
  /** Callback when "Connect a calendar provider" button is clicked in empty state */
  onConnectClick?: () => void
}

export function CalendarList({
  selectedCalendarId = null,
  onChange,
  className,
  disabled = false,
  isSelectable = false,
  isLocked = false,
  showDisconnect = false,
  onConnectClick,
}: CalendarListProps) {
  const { integrations, loading, refreshCalendarsForIntegration, reconnect, disconnect } = useCalendars()
  const [refreshingSections, setRefreshingSections] = useState<Set<string>>(new Set())
  const [reconnectingSections, setReconnectingSections] = useState<Set<string>>(new Set())
  const [disconnectingSections, setDisconnectingSections] = useState<Set<string>>(new Set())
  const [confirmDisconnect, setConfirmDisconnect] = useState<{ integrationId: string; providerEmail: string } | null>(null)

  // Create composite key: integration_id:calendar_id
  const getCompositeKey = (integrationId: string, calendarId: string) => {
    return `${integrationId}:${calendarId}`
  }

  const handleCalendarChange = (compositeKey: string) => {
    if (!onChange || isLocked) return
    
    // If clicking the same calendar, deselect it (allow null)
    if (selectedCalendarId === compositeKey) {
      onChange(null)
    } else {
      onChange(compositeKey)
    }
  }

  const handleRefreshSection = async (integrationId: string) => {
    if (!integrationId) return

    try {
      setRefreshingSections(prev => new Set(prev).add(integrationId))
      await refreshCalendarsForIntegration(integrationId)
      toast.success('Calendars refreshed successfully')
    } catch (error) {
      console.error('Error refreshing calendars:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to refresh calendars')
    } finally {
      setRefreshingSections(prev => {
        const next = new Set(prev)
        next.delete(integrationId)
        return next
      })
    }
  }

  const handleReconnect = async (integrationId: string, providerName: string) => {
    if (!integrationId) return

    try {
      setReconnectingSections(prev => new Set(prev).add(integrationId))
      await reconnect(integrationId)
      toast.success(`${providerName} reconnected successfully!`)
    } catch (error) {
      console.error('Error reconnecting:', error)
      // Don't show error for popup closed - user cancelled intentionally
      if (error instanceof Error && error.message !== 'OAuth popup was closed') {
        toast.error(error.message || 'Failed to reconnect')
      }
    } finally {
      setReconnectingSections(prev => {
        const next = new Set(prev)
        next.delete(integrationId)
        return next
      })
    }
  }

  const handleDisconnectClick = (integrationId: string, providerEmail: string) => {
    setConfirmDisconnect({ integrationId, providerEmail })
  }

  const handleDisconnectConfirm = async () => {
    if (!confirmDisconnect) return

    const { integrationId, providerEmail } = confirmDisconnect
    setConfirmDisconnect(null)

    try {
      setDisconnectingSections(prev => new Set(prev).add(integrationId))
      await disconnect(integrationId)
      toast.success(`Disconnected ${providerEmail}`)
    } catch (error) {
      console.error('Error disconnecting:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to disconnect: ${errorMessage}`)
    } finally {
      setDisconnectingSections(prev => {
        const next = new Set(prev)
        next.delete(integrationId)
        return next
      })
    }
  }

  if (loading) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="text-sm text-muted-foreground">Loading calendars...</div>
      </div>
    )
  }

  // Check if there are any calendars across all integrations
  const hasCalendars = integrations.some(integration => integration.calendars.length > 0)
  
  if (!hasCalendars) {
    return (
      <div className={cn('flex flex-col items-center p-10', className)}>
        <CalendarAddIcon className="h-16 w-16 text-muted-foreground mb-4" />
        <div className="text-center text-muted-foreground">
          <p className="mb-4">No calendars found.</p>
          {onConnectClick ? (
            <Button onClick={onConnectClick}>
              Connect a calendar provider
            </Button>
          ) : (
            <p className="text-sm">Please connect an account via settings {'=>'} Calendar integrations.</p>
          )}
        </div>
      </div>
    )
  }

  // Sort calendars within an integration: primary first, read-only last, then alphabetically
  const sortCalendars = (calendars: CalendarResource[]) => {
    return [...calendars].sort((a, b) => {
      const aIsReader = a.accessRole === 'reader'
      const bIsReader = b.accessRole === 'reader'
      
      // Primary calendars first
      if (a.primary && !b.primary) return -1
      if (!a.primary && b.primary) return 1
      
      // Read-only calendars last
      if (aIsReader && !bIsReader) return 1
      if (!aIsReader && bIsReader) return -1
      
      // Alphabetical by summary
      return (a.summary || '').localeCompare(b.summary || '')
    })
  }

  // Render calendar item content (shared between selectable and non-selectable modes)
  const renderCalendarContent = (calendar: CalendarResource, isReader: boolean) => (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {/* Calendar color indicator */}
      {calendar.backgroundColor && (
        <div
          className="w-3 h-3 rounded-full flex-shrink-0 border border-border"
          style={{ backgroundColor: calendar.backgroundColor }}
        />
      )}

      {/* Calendar name and tags */}
      <div className="flex-1 min-w-0 flex flex-col gap-1 md:flex-row md:items-center md:gap-1.5">
        {/* Calendar name - truncate to prevent overflow */}
        <span className="text-sm truncate min-w-0">
          {calendar.summary}
        </span>
        
        {/* Tags - below on mobile, inline on desktop */}
        <div className="flex flex-wrap items-center gap-1.5 md:flex-shrink-0">
          {calendar.primary && (
            <span className="text-xs px-1.5 py-0.5 rounded-md bg-secondary text-secondary-foreground whitespace-nowrap">
              Primary
            </span>
          )}
          {calendar.automationIsActive && (
            <span className="text-xs px-1.5 py-0.5 rounded-md bg-primary/10 text-primary whitespace-nowrap flex items-center gap-1">
              <LightningIcon className="h-3 w-3" />
              Automation
            </span>
          )}
          {calendar.analyticsIsActive && (
            <span className="text-xs px-1.5 py-0.5 rounded-md bg-primary/10 text-primary whitespace-nowrap flex items-center gap-1">
              {calendar.planningOnly ? (
                <CompassIcon className="h-3 w-3" />
              ) : (
                <InsightsIcon className="h-3 w-3" />
              )}
              {calendar.planningOnly ? 'Planning' : 'Analytics'}
            </span>
          )}
          {isReader && (
            <span className="text-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground whitespace-nowrap">
              Read-only
            </span>
          )}
        </div>
      </div>
    </div>
  )

  // Filter integrations with calendars
  const integrationsWithCalendars = integrations.filter(integration => integration.calendars.length > 0)

  // Non-selectable mode: simple list without radio buttons
  if (!isSelectable) {
    return (
      <div className={cn('space-y-4 pb-0', disabled && 'opacity-60 pointer-events-none', className)}>
        <Card className="py-0 gap-0 bg-card text-card-foreground overflow-hidden">
          {integrationsWithCalendars.map((integration) => {
            const isRefreshing = refreshingSections.has(integration.id)
            const isReconnecting = reconnectingSections.has(integration.id)
            const sortedCalendars = sortCalendars(integration.calendars)

            return (
              <div key={integration.id}>
                {/* Section header with bottom border */}
                <div className="px-4 py-3 space-y-1.5 border-b border-border">
                  {/* Row 1: Provider logo, email, and action buttons */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {/* Provider logo */}
                      {integration.provider.logo && (
                        <img
                          src={integration.provider.logo}
                          alt={integration.provider.display_name}
                          className="w-4 h-4 rounded-sm flex-shrink-0 mt-0.5"
                        />
                      )}
                      {/* Provider email - allows text wrapping */}
                      <span className="text-sm font-bold text-foreground break-all">
                        {integration.provider_email}
                      </span>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {integration.isDisconnected ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleReconnect(integration.id, integration.provider.display_name)}
                          disabled={isReconnecting || disabled}
                          className="h-7 px-3 text-xs"
                        >
                          {isReconnecting ? (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            'Reconnect'
                          )}
                        </Button>
                      ) : (
                        <>
                          {/* Remove button (left of Refresh) */}
                          {showDisconnect && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleDisconnectClick(integration.id, integration.provider_email)}
                              disabled={disconnectingSections.has(integration.id) || disabled}
                              className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              {disconnectingSections.has(integration.id) ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="h-3 w-3 md:mr-1" />
                                  <span className="hidden md:inline">Remove</span>
                                </>
                              )}
                            </Button>
                          )}
                          {/* Refresh button */}
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => handleRefreshSection(integration.id)}
                            disabled={isRefreshing || disabled}
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <RefreshCw className={`h-3 w-3 md:mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden md:inline">Refresh</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Disconnected tag (only if disconnected) */}
                  {integration.isDisconnected && (
                    <div className="pl-6">
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-destructive/10 text-destructive">
                        Disconnected
                      </span>
                    </div>
                  )}
                </div>
              
                {/* Calendars in this integration */}
                <div className="space-y-1 px-2 py-3">
                  {sortedCalendars.map((calendar) => {
                    const uniqueKey = `${integration.id}-${calendar.id}`
                    const isReader = calendar.accessRole === 'reader'
                    const isItemDisabled = isReader || disabled || integration.isDisconnected

                    return (
                      <div
                        key={uniqueKey}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2',
                          isItemDisabled && 'opacity-50'
                        )}
                      >
                        {renderCalendarContent(calendar, isReader)}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </Card>

        {/* Confirmation Dialog for Remove */}
        <Dialog open={!!confirmDisconnect} onOpenChange={(open) => !open && setConfirmDisconnect(null)}>
          <DialogContent hideClose className="sm:max-w-md">
            <DialogHeader className="text-center">
              <DialogTitle className="text-center">Remove account?</DialogTitle>
              <DialogDescription className="text-center">
                Are you sure you want to remove <span className="font-medium text-foreground">{confirmDisconnect?.providerEmail}</span>? 
                This will remove all its calendars and associated automation rules, life domains mappings and analytics.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-row gap-2 justify-center">
              <Button 
                variant="outline" 
                onClick={() => setConfirmDisconnect(null)}
                className="flex-1 sm:flex-initial min-w-[120px]"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDisconnectConfirm}
                className="flex-1 sm:flex-initial min-w-[120px]"
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Selectable mode: clickable calendar items
  return (
    <div className={cn('space-y-4 pb-0', disabled && 'opacity-60 pointer-events-none', className)}>
      <Card className="py-0 gap-0 bg-card text-card-foreground overflow-hidden">
        {integrationsWithCalendars.map((integration) => {
          const isRefreshing = refreshingSections.has(integration.id)
          const isReconnecting = reconnectingSections.has(integration.id)
          const sortedCalendars = sortCalendars(integration.calendars)

          return (
            <div key={integration.id}>
              {/* Section header with bottom border */}
              <div className="px-4 py-3 space-y-1.5 border-b border-border">
                {/* Row 1: Provider logo, email, and action buttons */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {/* Provider logo */}
                    {integration.provider.logo && (
                      <img
                        src={integration.provider.logo}
                        alt={integration.provider.display_name}
                        className="w-4 h-4 rounded-sm flex-shrink-0 mt-0.5"
                      />
                    )}
                    {/* Provider email - allows text wrapping */}
                    <span className="text-sm font-bold text-foreground break-all">
                      {integration.provider_email}
                    </span>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {integration.isDisconnected ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleReconnect(integration.id, integration.provider.display_name)}
                        disabled={isReconnecting || disabled}
                        className="h-7 px-3 text-xs"
                      >
                        {isReconnecting ? (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          'Reconnect'
                        )}
                      </Button>
                    ) : (
                      <>
                        {/* Remove button (left of Refresh) */}
                        {showDisconnect && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => handleDisconnectClick(integration.id, integration.provider_email)}
                            disabled={disconnectingSections.has(integration.id) || disabled}
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            {disconnectingSections.has(integration.id) ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="h-3 w-3 md:mr-1" />
                                <span className="hidden md:inline">Remove</span>
                              </>
                            )}
                          </Button>
                        )}
                        {/* Refresh button */}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleRefreshSection(integration.id)}
                          disabled={isRefreshing || disabled}
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <RefreshCw className={`h-3 w-3 md:mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                          <span className="hidden md:inline">Refresh</span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Row 2: Disconnected tag (only if disconnected) */}
                {integration.isDisconnected && (
                  <div className="pl-6">
                    <span className="text-xs px-1.5 py-0.5 rounded-md bg-destructive/10 text-destructive">
                      Disconnected
                    </span>
                  </div>
                )}
              </div>
            
              {/* Calendars in this integration */}
              <div className="space-y-1 px-2 py-3">
                {sortedCalendars.map((calendar) => {
                  const compositeKey = getCompositeKey(integration.id, calendar.id)
                  const isSelected = selectedCalendarId === compositeKey
                  const uniqueKey = `${integration.id}-${calendar.id}`
                  const isReader = calendar.accessRole === 'reader'
                  const isItemDisabled = isReader || disabled || integration.isDisconnected || isLocked

                  return (
                    <div
                      key={uniqueKey}
                      onClick={() => !isItemDisabled && handleCalendarChange(compositeKey)}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-md px-3 py-2 transition-colors',
                        isItemDisabled
                          ? 'cursor-not-allowed opacity-50' 
                          : 'cursor-pointer hover:bg-accent',
                        isSelected && !isItemDisabled && 'bg-accent',
                        isSelected && isLocked && 'bg-accent opacity-100'
                      )}
                    >
                      {/* Calendar content */}
                      <div className="flex-1 min-w-0">
                        {renderCalendarContent(calendar, isReader)}
                      </div>
                      
                      {/* Arrow icon */}
                      <div className="flex-shrink-0 text-muted-foreground">
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </Card>

      {/* Confirmation Dialog for Remove */}
      <Dialog open={!!confirmDisconnect} onOpenChange={(open) => !open && setConfirmDisconnect(null)}>
        <DialogContent hideClose className="sm:max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center">Remove calendar account?</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to remove <span className="font-medium text-foreground">{confirmDisconnect?.providerEmail}</span>? 
              This will disconnect all calendars associated with this account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 justify-center">
            <Button 
              variant="outline" 
              onClick={() => setConfirmDisconnect(null)}
              className="flex-1 sm:flex-initial min-w-[120px]"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDisconnectConfirm}
              className="flex-1 sm:flex-initial min-w-[120px]"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
